import type { SiteSettings } from '@brix/types'

const FONT_PRESETS = [
  { id: 'modern',    label: 'Modern',    heading: 'Inter',              body: 'Inter' },
  { id: 'editorial', label: 'Editorial', heading: 'Playfair Display',   body: 'Lato' },
  { id: 'elegant',   label: 'Elegant',   heading: 'Cormorant Garamond', body: 'Raleway' },
  { id: 'technical', label: 'Technical', heading: 'JetBrains Mono',     body: 'IBM Plex Sans' },
  { id: 'friendly',  label: 'Friendly',  heading: 'Nunito',             body: 'Open Sans' },
  { id: 'bold',      label: 'Bold',      heading: 'Oswald',             body: 'Source Sans Pro' },
]

const RADIUS_OPTIONS = [
  { id: 'sharp',   label: 'Sharp',   preview: '2px' },
  { id: 'default', label: 'Default', preview: '12px' },
  { id: 'rounded', label: 'Rounded', preview: '20px' },
] as const

const THEME_PRESETS = [
  { id: 'bold-agency',  label: 'Bold Agency',  color: '#dc2626', fontPreset: 'bold',      heading: 'Oswald',              body: 'Source Sans Pro', borderRadius: 'sharp'   as const, desc: 'High-impact & direct' },
  { id: 'minimal-saas', label: 'Minimal SaaS', color: '#0f172a', fontPreset: 'modern',    heading: 'Inter',               body: 'Inter',           borderRadius: 'default' as const, desc: 'Clean & professional' },
  { id: 'editorial',    label: 'Editorial',    color: '#0d9488', fontPreset: 'editorial', heading: 'Playfair Display',    body: 'Lato',            borderRadius: 'default' as const, desc: 'Elegant & editorial' },
  { id: 'friendly',     label: 'Friendly',     color: '#f59e0b', fontPreset: 'friendly',  heading: 'Nunito',              body: 'Open Sans',       borderRadius: 'rounded' as const, desc: 'Warm & approachable' },
  { id: 'tech-dark',    label: 'Tech Dark',    color: '#6366f1', fontPreset: 'technical', heading: 'JetBrains Mono',      body: 'IBM Plex Sans',   borderRadius: 'sharp'   as const, desc: 'Technical & precise' },
  { id: 'luxury',       label: 'Luxury',       color: '#92400e', fontPreset: 'elegant',   heading: 'Cormorant Garamond',  body: 'Raleway',         borderRadius: 'default' as const, desc: 'Sophisticated & exclusive' },
]

const NAV_STYLES: { id: SiteSettings['navStyle']; label: string; desc: string }[] = [
  { id: 'light',       label: 'Light',       desc: 'White background, dark text' },
  { id: 'dark',        label: 'Dark',        desc: 'Black background, white text' },
  { id: 'transparent', label: 'Transparent', desc: 'Floats over hero, dark on scroll' },
]

export type ThemeSettings = Pick<
  SiteSettings,
  'primaryColor' | 'fontPreset' | 'fontHeading' | 'fontBody' | 'borderRadius' | 'navStyle'
>

// Site-wide visual design: theme presets, primary color, typography, border
// radius, nav style. Shared between PagesPage's site settings panel and the
// "Design" modal inside the block editor (EditorToolbar) — same pattern as
// TemplatePicker.tsx being shared between CreateSiteModal and CreatePageModal.
export function ThemeEditor({ value, onChange }: { value: ThemeSettings; onChange: (patch: Partial<SiteSettings>) => void }) {
  return (
    <div className="space-y-8">
      {/* Nav style */}
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900">Nav style</h2>
        <p className="mb-3 text-xs text-gray-400">How the navigation bar looks on your published site.</p>
        <div className="grid grid-cols-3 gap-2">
          {NAV_STYLES.map(opt => (
            <button
              key={opt.id}
              onClick={() => onChange({ navStyle: opt.id })}
              className={`rounded-xl border p-3 text-left transition-all ${
                (value.navStyle ?? 'light') === opt.id
                  ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <p className="text-xs font-semibold text-gray-900">{opt.label}</p>
              <p className="mt-0.5 text-[10px] text-gray-400 leading-snug">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Theme picker */}
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900">Site style</h2>
        <p className="mb-3 text-xs text-gray-400">Pick a starting point. Fine-tune the details below.</p>
        <div className="grid grid-cols-2 gap-2">
          {THEME_PRESETS.map(preset => {
            const active = value.primaryColor === preset.color && value.fontPreset === preset.fontPreset
            return (
              <button
                key={preset.id}
                aria-label={preset.label}
                onClick={() => onChange({
                  primaryColor: preset.color,
                  fontPreset: preset.fontPreset,
                  fontHeading: preset.heading,
                  fontBody: preset.body,
                  borderRadius: preset.borderRadius,
                })}
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-all ${
                  active
                    ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <span
                  className="mt-0.5 h-5 w-5 shrink-0 rounded-full border border-black/10"
                  style={{ backgroundColor: preset.color }}
                />
                <span>
                  <span className="block text-xs font-semibold text-gray-900">{preset.label}</span>
                  <span className="block text-[10px] text-gray-400">{preset.desc}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* Primary color */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Primary color</h2>
        <input
          type="color"
          aria-label="Primary color"
          value={value.primaryColor ?? '#6d28d9'}
          onChange={e => onChange({ primaryColor: e.target.value })}
          className="h-9 w-16 cursor-pointer rounded-lg border border-gray-200 p-1"
        />
      </div>

      {/* Typography */}
      <div>
        <h2 className="mb-1 text-sm font-semibold text-gray-900">Typography</h2>
        <p className="mb-3 text-xs text-gray-400">Set the typographic style for your site.</p>
        <div className="grid grid-cols-2 gap-2">
          {FONT_PRESETS.map(preset => (
            <button
              key={preset.id}
              onClick={() => onChange({ fontPreset: preset.id, fontHeading: preset.heading, fontBody: preset.body })}
              className={`rounded-xl border p-3 text-left transition-all ${
                (value.fontPreset ?? 'modern') === preset.id
                  ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <p className="text-xs font-semibold text-gray-900">{preset.label}</p>
              <p className="mt-0.5 text-[10px] text-gray-400 truncate">{preset.heading} / {preset.body}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Border radius */}
      <div>
        <h2 className="mb-3 text-sm font-semibold text-gray-900">Border style</h2>
        <div className="flex gap-3">
          {RADIUS_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => onChange({ borderRadius: opt.id })}
              className={`flex flex-1 flex-col items-center gap-2 rounded-xl border p-3 transition-all ${
                (value.borderRadius ?? 'default') === opt.id
                  ? 'border-violet-500 bg-violet-50 ring-1 ring-violet-500'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div
                className="h-8 w-12 border-2 border-gray-400 bg-gray-100"
                style={{ borderRadius: opt.preview }}
              />
              <span className="text-xs font-medium text-gray-700">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
